import { supabase } from '../lib/supabase';

export interface InvoiceSettings {
  id?: string;
  business_id: string;
  
  // Company Information
  company_name?: string;
  company_address?: string;
  company_phone?: string;
  company_email?: string;
  company_website?: string;
  
  // Invoice Styling
  primary_color: string;
  header_background: string;
  text_color: string;
  accent_color: string;
  
  // Logo Settings
  include_logo: boolean;
  logo_url?: string;
  
  // Invoice Options
  show_item_descriptions: boolean;
  show_tax_breakdown: boolean;
  show_vehicle: boolean;
  auto_numbering: boolean;
  invoice_prefix: string;
  
  // Invoice Terms
  payment_terms: string;
  /** Structured default term used to compute invoice due dates: due_on_receipt | net_7 | net_15 | net_30 | net_60 */
  default_payment_terms?: string;
  late_fees_policy: string;
  thank_you_message: string;
  
  // Stripe Settings
  request_tips_at_checkout?: boolean;

  // After-payment notifications
  send_sms_after_payment?: boolean;
  sms_after_payment_message?: string;
  send_email_after_payment?: boolean;
  email_after_payment_message?: string;
  
  // Timestamps
  created_at?: string;
  updated_at?: string;
}

export const defaultInvoiceSettings: Partial<InvoiceSettings> = {
  primary_color: '#3B82F6',
  header_background: '#F8FAFC',
  text_color: '#374151',
  accent_color: '#059669',
  include_logo: false,
  show_item_descriptions: true,
  show_tax_breakdown: true,
  show_vehicle: true,
  auto_numbering: true,
  invoice_prefix: 'INV-',
  payment_terms: 'On receipt',
  default_payment_terms: 'due_on_receipt',
  late_fees_policy: '1.5% monthly service charge on overdue amounts',
  thank_you_message: 'Thank you for your business!',
  request_tips_at_checkout: false,
  send_sms_after_payment: false,
  sms_after_payment_message: '',
  send_email_after_payment: false,
  email_after_payment_message: '',
};

export class InvoiceSettingsService {
  /**
   * Get invoice settings for a business
   */
  static async getInvoiceSettings(businessId: string): Promise<InvoiceSettings | null> {
    try {
      if (!supabase) {
        console.error('Supabase client not initialized');
        return null;
      }

      const { data, error } = await supabase
        .from('invoice_settings')
        .select('*')
        .eq('business_id', businessId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No settings found, return null to indicate default settings should be used
          console.log('No invoice settings found for business:', businessId);
          return null;
        }
        throw error;
      }

      return data as InvoiceSettings;
    } catch (error) {
      console.error('Error fetching invoice settings:', error);
      return null;
    }
  }

  /**
   * Create or update invoice settings for a business
   */
  static async saveInvoiceSettings(businessId: string, settings: Partial<InvoiceSettings>): Promise<boolean> {
    try {
      if (!supabase) {
        console.error('Supabase client not initialized');
        return false;
      }

      // First check if settings already exist
      const { data: existingSettings } = await supabase
        .from('invoice_settings')
        .select('id')
        .eq('business_id', businessId)
        .single();

      if (existingSettings) {
        // Update existing settings
        const { error } = await supabase
          .from('invoice_settings')
          .update({
            ...settings,
            updated_at: new Date().toISOString()
          })
          .eq('business_id', businessId);

        if (error) throw error;
      } else {
        // Create new settings
        const { error } = await supabase
          .from('invoice_settings')
          .insert({
            business_id: businessId,
            ...defaultInvoiceSettings,
            ...settings,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) throw error;
      }

      return true;
    } catch (error) {
      console.error('Error saving invoice settings:', error);
      return false;
    }
  }
} 